<template>
  <div
    class="tc-player-wrapper"
    :class="{ 'hide-controls': !controls }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    ref="wrapperRef"
  >
    <!-- Header 覆盖层 -->
    <div
      v-if="showHeader"
      class="player-header"
      :class="{ show: showHeaderOverlay }"
    >
      <div class="header-content">
        <div
          class="header-left"
          :class="{ 'cursor-pointer': navigateOnHeader }"
          @click="handleHeaderNavigate"
        >
          <h3 class="video-title">枫运直播</h3>
          <span class="live-indicator">● 直播中</span>
        </div>
        <div class="header-right">
          <div class="viewer-count">
            <svg class="viewer-icon" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
              />
            </svg>
            <span>1,234 观看</span>
          </div>
          <div class="quality-selector">
            <select class="quality-select">
              <option value="auto">自动</option>
              <option value="1080p">1080P</option>
              <option value="720p">720P</option>
              <option value="480p">480P</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <video
      :id="playerId"
      :width="width"
      :height="height"
      :preload="preload"
      :playsinline="playsinline"
      :webkit-playsinline="webkitPlaysinline"
      :x5-playsinline="x5Playsinline"
      :x5-video-player-type="x5VideoPlayerType"
      :x5-video-player-fullscreen="x5VideoPlayerFullscreen"
      :x5-video-orientation="x5VideoOrientation"
      :poster="poster"
      :controls="controls"
    ></video>

    <!-- 加载动画覆盖层 -->
    <div v-if="isLoading && showLoadingAnimation" class="loading-overlay">
      <div class="loading-container">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <div class="loading-text">正在加载视频...</div>
        <div class="loading-progress">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: loadingProgress + '%' }"
            ></div>
          </div>
          <div class="progress-text">{{ Math.round(loadingProgress) }}%</div>
        </div>
      </div>
    </div>

    <!-- 覆盖层容器 -->
    <div
      v-if="
        (showEnterButton && showEnterOverlay) ||
        (showUnmuteButton && showUnmuteOverlay)
      "
      class="overlay-container"
    >
      <!-- 进入直播间按钮（居中） -->
      <div
        v-if="showEnterButton && showEnterOverlay"
        class="enter-button-wrapper"
      >
        <div class="enter-button" @click="handleEnterLive">
          <span class="button-text">进入直播间</span>
        </div>
      </div>

      <!-- 取消静音按钮（底部） -->
      <div
        v-if="showUnmuteButton && showUnmuteOverlay"
        class="unmute-button-wrapper"
      >
        <div class="unmute-button" @click="unmute">
          <svg class="button-icon" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
            />
          </svg>
          <span class="button-text">点击取消静音</span>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="isFloating"
    class="fixed right-4 bottom-4 z-10000 h-[202px] w-[360px] overflow-hidden rounded-lg"
  >
    <video :id="miniPlayerId" :key="miniKey" class="h-full w-full"></video>
  </div>
  <div
    v-if="debugHudVisible"
    class="fixed right-[380px] bottom-4 z-10001 max-h-[240px] w-[340px] rounded-md bg-black/80 p-2 text-xs text-white shadow-lg"
  >
    <div class="mb-1 flex items-center justify-between">
      <div class="font-semibold">TCPlayer Debug</div>
      <div class="flex gap-2">
        <button
          class="rounded bg-white/10 px-2 py-1 hover:bg-white/20"
          @click="debugHudVisible = false"
        >
          隐藏
        </button>
        <button
          class="rounded bg-white/10 px-2 py-1 hover:bg-white/20"
          @click="debugLogs = []"
        >
          清空
        </button>
      </div>
    </div>
    <div class="max-h-[200px] space-y-1 overflow-auto">
      <div v-for="(l, i) in debugLogs" :key="i" class="leading-5">{{ l }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  TCPlayerConfig,
  TCPlayerInstance,
  TCPlayerEvent,
  DanmuMessage,
} from "~/types/tcplayer";

// Props 定义
interface Props {
  src?: string;
  poster?: string;
  width?: number | string;
  height?: number | string;
  autoplay?: boolean;
  controls?: boolean;
  preload?: "auto" | "meta" | "none";
  playsinline?: boolean;
  webkitPlaysinline?: boolean;
  x5Playsinline?: boolean;
  x5VideoPlayerType?: string;
  x5VideoPlayerFullscreen?: boolean;
  x5VideoOrientation?: string;
  licenseUrl: string;
  fileID?: string;
  appID?: string;
  psign?: string;
  showDanmu?: boolean;
  danmuApi?: string;
  websocketUrl?: string;
  defaultUnmute?: boolean; // 默认取消静音
  showUnmuteButton?: boolean; // 显示取消静音按钮
  showEnterButton?: boolean; // 显示进入直播间按钮
  livePageUrl?: string; // 直播间页面URL
  showLoadingAnimation?: boolean; // 是否显示加载动画
  showHeader?: boolean; // 是否显示顶部 header
  liveId?: string;
  navigateOnHeader?: boolean;
  debugHud?: boolean;
  isFloating?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  width: "100%",
  height: "270px",
  autoplay: false,
  controls: true,
  preload: "auto",
  playsinline: true,
  webkitPlaysinline: true,
  x5Playsinline: true,
  x5VideoPlayerType: "h5",
  x5VideoPlayerFullscreen: true,
  x5VideoOrientation: "portraint",
  showDanmu: false,
  defaultUnmute: true, // 默认取消静音
  showUnmuteButton: false, // 显示取消静音按钮（默认不显示）
  showEnterButton: false, // 显示进入直播间按钮（默认不显示）
  livePageUrl: "/live", // 默认直播间URL
  showLoadingAnimation: false, // 默认显示加载动画
  showHeader: false, // 默认显示顶部 header
  liveId: undefined,
  navigateOnHeader: false,
  debugHud: false,
  isFloating: false,
});

// Emits 定义
const emit = defineEmits<{
  ready: [];
  play: [];
  pause: [];
  ended: [];
  error: [error: string];
  timeupdate: [time: number];
  loadstart: [];
  loadeddata: [];
  loadedmetadata: [];
  canplay: [];
  canplaythrough: [];
  progress: [];
  seeking: [];
  seeked: [];
  volumechange: [];
  ratechange: [];
  resize: [];
  fullscreenchange: [];
}>();

// 响应式数据
const playerId = ref("tc-player-container");
const miniPlayerId = ref("tc-mini-player");
const miniIndex = ref(0);
const miniKey = ref<string | number>(0);
const player = ref<TCPlayerInstance | null>(null);
const miniPlayer = ref<TCPlayerInstance | null>(null);
const isReady = ref(false);
const isMuted = ref(true); // 默认静音状态
const showUnmuteOverlay = ref(false); // 显示取消静音覆盖层
const showEnterOverlay = ref(false); // 显示进入直播间覆盖层
const showHeaderOverlay = ref(false); // 显示header覆盖层（鼠标悬停时）
const isHovering = ref(false); // 鼠标悬停状态
const isLoading = ref(true); // 显示加载动画
const loadingProgress = ref(0); // 加载进度
const wrapperRef = ref<HTMLElement | null>(null);
const isInView = ref(true);
const isFloating = ref(props.isFloating);
let io: IntersectionObserver | null = null;
let lastTime = 0;
const isSwitching = ref(false);

const debugHudVisible = ref(Boolean(props.debugHud));
let keyHandler: any;
const debugLogs = ref<string[]>([]);
const ts = () => new Date().toLocaleTimeString();
const pushLog = (prefix: string, msg: string) => {
  const line = `${ts()} ${prefix} ${msg}`;
  debugLogs.value.push(line);
  if (debugLogs.value.length > 200)
    debugLogs.value.splice(0, debugLogs.value.length - 200);
};
const log = (...args: any[]) => {
  console.log("[TCPlayer]", ...args);
  if (debugHudVisible.value)
    pushLog(
      "[TCPlayer]",
      args.map((a) => (typeof a === "string" ? a : String(a))).join(" "),
    );
};
const logMini = (...args: any[]) => {
  console.log("[TCPlayer:mini]", ...args);
  if (debugHudVisible.value)
    pushLog(
      "[TCPlayer:mini]",
      args.map((a) => (typeof a === "string" ? a : String(a))).join(" "),
    );
};

// 播放器配置
const getPlayerConfig = (): TCPlayerConfig => {
  const config: TCPlayerConfig = {
    licenseUrl: props.licenseUrl,
    // 不设置固定尺寸，让CSS控制
    // width: props.width,
    // height: props.height,
    poster: props.poster,
    autoplay: props.autoplay,
    controls: props.controls,
    preload: props.preload,
    playsinline: props.playsinline,
    webkitPlaysinline: props.webkitPlaysinline,
    x5Playsinline: props.x5Playsinline,
    x5VideoPlayerType: props.x5VideoPlayerType,
    x5VideoPlayerFullscreen: props.x5VideoPlayerFullscreen,
    x5VideoOrientation: props.x5VideoOrientation,
    // 如果需要自动播放，必须静音（浏览器策略）
    muted: props.autoplay ? true : false,
  };

  // 根据是否有 fileID 决定使用哪种播放方式
  if (props.fileID && props.appID && props.psign) {
    // 使用 FileID 播放（点播）
    config.fileID = props.fileID;
    config.appID = props.appID;
    config.psign = props.psign;
  } else if (props.src) {
    // 使用 URL 播放（直播或点播）
    config.sources = [
      {
        src: props.src,
        type: getVideoType(props.src),
      },
    ];
  }

  // 弹幕配置
  if (props.showDanmu) {
    config.barrage = {
      enable: true,
      websocket: props.websocketUrl,
    } as any;
  }

  return config;
};

const getMiniConfig = (): TCPlayerConfig => {
  const c = getPlayerConfig();
  c.autoplay = true;
  c.controls = false;
  (c as any).muted = true;
  return c;
};

// 获取视频类型
const getVideoType = (src: string): string => {
  if (src.includes(".m3u8")) return "application/x-mpegURL";
  if (src.includes(".flv")) return "video/x-flv";
  if (src.includes(".mp4")) return "video/mp4";
  if (src.includes(".webm")) return "video/webm";
  return "video/mp4";
};

// 初始化播放器
const initPlayer = async () => {
  try {
    // 等待 TCPlayer 加载完成
    if (typeof window.TCPlayer === "undefined") {
      console.warn("TCPlayer not loaded, retrying...");
      setTimeout(initPlayer, 100);
      return;
    }

    // 确保DOM元素存在
    const videoElement = document.getElementById(playerId.value);
    if (!videoElement) {
      console.warn("Video element not found, retrying...");
      setTimeout(initPlayer, 100);
      return;
    }

    // 销毁已存在的播放器
    if (player.value) {
      try {
        // 检查 destroy 方法是否存在且是函数
        if (typeof player.value.destroy === "function") {
          player.value.destroy();
        }
      } catch (e) {
        console.warn("Error destroying existing player:", e);
      } finally {
        player.value = null;
      }
    }

    // 创建新播放器实例
    const config = getPlayerConfig();
    log("init main with config", config);

    player.value = window.TCPlayer(playerId.value, config);

    // 初始化弹幕插件
    if (props.showDanmu && window.TcplayerBarragePlugin) {
      try {
        console.log("Initializing barrage plugin...");
        // 等待播放器完全初始化后再初始化弹幕插件
        player.value?.ready(() => {
          setTimeout(() => {
            try {
              // 按照官方示例的方式初始化弹幕插件
              const p = player.value;
              if (!p) return;
              const tcplayerBarrage = new window.TcplayerBarragePlugin(p);
              tcplayerBarrage.init();
              p.tcplayerBarrage = tcplayerBarrage;

              // 加载示例弹幕数据
              const sampleDanmu = [
                {
                  mode: 1,
                  text: "欢迎来到枫运直播！",
                  stime: 2000,
                  size: 25,
                  color: "#FFFFFF",
                },
                {
                  mode: 1,
                  text: "弹幕功能已启用",
                  stime: 5000,
                  size: 25,
                  color: "#00FF00",
                },
              ];

              tcplayerBarrage.load(sampleDanmu);
              tcplayerBarrage.start();

              console.log("Barrage plugin initialized successfully");
            } catch (error) {
              console.error("Failed to initialize barrage plugin:", error);
            }
          }, 500);
        });
      } catch (error) {
        console.error("Failed to initialize barrage plugin:", error);
      }
    }

    // 绑定事件
    bindEvents();

    // 等待播放器准备就绪
    player.value?.ready(() => {
      isReady.value = true;
      emit("ready");
      log("main ready");

      // 如果启用加载动画，显示加载动画
      if (props.showLoadingAnimation) {
        startLoadingAnimation();
      } else {
        // 不显示加载动画，直接处理自动播放
        isLoading.value = false;

        if (props.autoplay) {
          // 尝试静音自动播放
          setTimeout(() => {
            if (player.value) {
              try {
                player.value.muted(true);
                player.value.play();
                log("main auto play (muted)");
              } catch (error) {
                console.error("[TCPlayer] auto play failed", error);
              }
            }
          }, 100);
        }
      }

      // 默认取消静音（仅在 defaultUnmute 为 true 且有加载动画时）
      if (props.defaultUnmute && props.showLoadingAnimation) {
        setTimeout(() => {
          if (player.value) {
            player.value.muted(false);
            isMuted.value = false;
            log("default unmute applied");
          }
        }, 500);
      }

      // 根据配置显示对应的覆盖层
      // 注意：进入直播间按钮只在悬停时显示，所以这里不设置

      if (props.showUnmuteButton && isMuted.value) {
        showUnmuteOverlay.value = true;
        log("show unmute overlay");
      }
    });
  } catch (error) {
    console.error("[TCPlayer] init main failed", error);
    emit("error", error instanceof Error ? error.message : "Unknown error");
  }
};

const initMiniPlayer = async () => {
  if (typeof window.TCPlayer === "undefined") {
    logMini("lib not loaded");
    return;
  }
  const el = document.getElementById(miniPlayerId.value);
  if (!el) {
    logMini("mini element missing");
    return;
  }
  if (miniPlayer.value && typeof miniPlayer.value.destroy === "function") {
    try {
      miniPlayer.value.destroy();
    } catch (e) {
      console.error("[TCPlayer:mini] destroy error", e);
    }
    miniPlayer.value = null;
  }
  const config = getMiniConfig();
  logMini("init with config", config);
  miniPlayer.value = window.TCPlayer(miniPlayerId.value, config);
  miniPlayer.value?.ready(() => {
    try {
      logMini("ready");
      miniPlayer.value?.muted(true);
      logMini("muted(true)");
    } catch (e) {
      console.error("[TCPlayer:mini] muted error", e);
    }
  });
  [
    "play",
    "pause",
    "ended",
    "error",
    "canplay",
    "loadedmetadata",
    "timeupdate",
    "progress",
  ].forEach((ev) => {
    try {
      miniPlayer.value?.on(ev as any, (...a: any[]) => logMini("event", ev, a));
    } catch {}
  });
  try {
    miniPlayer.value?.on("canplay" as any, () => {
      try {
        if (lastTime) miniPlayer.value?.currentTime(lastTime);
        miniPlayer.value?.play();
        logMini("canplay -> play");
      } catch (e) {
        console.error("[TCPlayer:mini] canplay play error", e);
      }
    });
    setTimeout(() => {
      try {
        miniPlayer.value?.play();
      } catch {}
    }, 200);
  } catch (e) {
    console.error("[TCPlayer:mini] bind canplay error", e);
  }
};

const destroyMiniPlayer = () => {
  if (miniPlayer.value) {
    try {
      typeof miniPlayer.value.destroy === "function" &&
        miniPlayer.value.destroy();
    } catch (e) {
      console.error("[TCPlayer:mini] destroy error", e);
    }
    miniPlayer.value = null;
  }
};

// 绑定播放器事件
const bindEvents = () => {
  if (!player.value) return;

  const events: TCPlayerEvent[] = [
    "loadstart",
    "loadeddata",
    "loadedmetadata",
    "canplay",
    "canplaythrough",
    "play",
    "pause",
    "ended",
    "error",
    "timeupdate",
    "progress",
    "seeking",
    "seeked",
    "volumechange",
    "ratechange",
    "resize",
    "fullscreenchange",
  ];

  events.forEach((event) => {
    player.value!.on(event, (...args: any[]) => {
      switch (event) {
        case "play":
          log("event play");
          emit("play");
          break;
        case "pause":
          log("event pause");
          emit("pause");
          break;
        case "ended":
          log("event ended");
          emit("ended");
          break;
        case "error":
          console.error("[TCPlayer] event error", args);
          emit("error", args[0]?.message || "Unknown error");
          break;
        case "timeupdate":
          emit("timeupdate", args[0] || 0);
          // log('event timeupdate', args[0])
          break;
        case "loadstart":
          emit("loadstart");
          break;
        case "loadeddata":
          emit("loadeddata");
          break;
        case "loadedmetadata":
          emit("loadedmetadata");
          break;
        case "canplay":
          emit("canplay");
          break;
        case "canplaythrough":
          emit("canplaythrough");
          break;
        case "progress":
          emit("progress");
          log("event progress");
          break;
        case "seeking":
          emit("seeking");
          break;
        case "seeked":
          emit("seeked");
          break;
        case "volumechange":
          emit("volumechange");
          // 更新静音状态
          if (player.value) {
            const muted = player.value.muted();
            if (typeof muted === "boolean") {
              isMuted.value = muted;

              // 更新取消静音覆盖层显示状态
              if (props.showUnmuteButton) {
                showUnmuteOverlay.value = muted;
              }
            }
          }
          break;
        case "ratechange":
          emit("ratechange");
          break;
        case "resize":
          emit("resize");
          log("event resize");
          break;
        case "fullscreenchange":
          emit("fullscreenchange");
          log("event fullscreenchange");
          break;
      }
    });
  });
};

// 监听 src 变化
watch(
  () => props.src,
  (newSrc) => {
    if (player.value && newSrc) {
      log("src changed", newSrc);
      player.value.src(newSrc);
    }
  },
  { immediate: false },
);

watch(isFloating, async (v) => {
  if (isSwitching.value) return;
  isSwitching.value = true;
  if (v) {
    miniIndex.value += 1;
    miniPlayerId.value = `tc-mini-player-${miniIndex.value}`;
    miniKey.value = miniPlayerId.value;
    await nextTick();
    await initMiniPlayer();
    try {
      lastTime = player.value ? player.value.currentTime() || 0 : 0;
      if (player.value && typeof player.value.pause === "function")
        player.value.pause();
      if (player.value && typeof player.value.destroy === "function")
        player.value.destroy();
      player.value = null;
    } catch {}
  } else {
    destroyMiniPlayer();
    try {
      miniKey.value = 0;
      await nextTick();
      await initPlayer();
      if (player.value) {
        player.value.ready(() => {
          try {
            if (lastTime) player.value!.currentTime(lastTime);
            player.value!.play();
          } catch {}
        });
      }
    } catch {}
  }
  isSwitching.value = false;
});

// 监听 poster 变化
watch(
  () => props.poster,
  (newPoster) => {
    if (player.value && newPoster) {
      // TCPlayer 可能需要重新初始化来更新 poster
      nextTick(() => {
        initPlayer();
      });
    }
  },
);

// 公开方法
const play = () => {
  if (player.value) {
    // 先显示加载动画
    startLoadingAnimation();
    // 加载动画完成后会自动播放（在startLoadingAnimation中处理）
  }
};

const pause = () => {
  if (player.value) {
    player.value.pause();
  }
};

const stop = () => {
  if (player.value) {
    player.value.stop();
  }
};

const setCurrentTime = (time: number) => {
  if (player.value) {
    player.value.currentTime(time);
  }
};

const getCurrentTime = (): number => {
  return player.value ? player.value.currentTime() || 0 : 0;
};

const setVolume = (volume: number) => {
  if (player.value) {
    player.value.volume(volume);
  }
};

const getVolume = (): number => {
  return player.value ? player.value.volume() || 1 : 1;
};

const setMuted = (muted: boolean) => {
  if (player.value) {
    player.value.muted(muted);
  }
};

const getIsMuted = (): boolean => {
  return player.value ? player.value.muted() || false : false;
};

// 取消静音方法
const unmute = () => {
  if (player.value) {
    player.value.muted(false);
    isMuted.value = false;
    showUnmuteOverlay.value = false;
    // 尝试播放视频
    try {
      player.value.play();
      console.log("Video started playing after unmute");
    } catch (error) {
      console.error("Failed to play video after unmute:", error);
    }
    console.log("Player unmuted");
  }
};

// 进入直播间方法
const handleEnterLive = () => {
  showEnterOverlay.value = false;
  navigateToLive();
};

const navigateToLive = () => {
  const base = props.livePageUrl || "/live";
  const url = props.liveId ? `${base}/${props.liveId}` : base;
  navigateTo(url);
};

const handleHeaderNavigate = () => {
  if (props.navigateOnHeader) {
    navigateToLive();
  }
};

// 鼠标悬停事件处理
const handleMouseEnter = () => {
  isHovering.value = true;

  // 显示 header（如果配置开启）
  if (props.showHeader) {
    showHeaderOverlay.value = true;
  }

  // 显示进入直播间按钮（如果配置开启）
  if (props.showEnterButton) {
    showEnterOverlay.value = true;
  }
};

const handleMouseLeave = () => {
  isHovering.value = false;

  // 隐藏 header
  showHeaderOverlay.value = false;

  // 隐藏进入直播间按钮
  if (props.showEnterButton) {
    showEnterOverlay.value = false;
  }
};

// 加载动画播放逻辑
const startLoadingAnimation = () => {
  console.log("Starting loading animation");
  isLoading.value = true;
  loadingProgress.value = 0;

  // 模拟加载进度
  const loadingInterval = setInterval(() => {
    loadingProgress.value += Math.random() * 8; // 减慢进度

    if (loadingProgress.value >= 100) {
      loadingProgress.value = 100;
      clearInterval(loadingInterval);

      // 加载完成后延迟一点时间再隐藏动画
      setTimeout(() => {
        console.log("Loading animation completed");
        isLoading.value = false;
        // 自动播放视频
        if (player.value) {
          console.log("Auto playing video after loading animation");
          try {
            player.value.play();
            console.log("Video started playing successfully");
          } catch (error) {
            console.error("Failed to auto play video:", error);
            console.log(
              "Browser blocked autoplay, showing unmute button for user interaction",
            );
            // 如果自动播放失败，显示取消静音按钮让用户手动播放
            if (props.showUnmuteButton) {
              showUnmuteOverlay.value = true;
            }
          }
        }
      }, 1000); // 增加延迟时间
    }
  }, 200); // 增加间隔时间
};

const setPlaybackRate = (rate: number) => {
  if (player.value) {
    player.value.playbackRate(rate);
  }
};

const getPlaybackRate = (): number => {
  return player.value ? player.value.playbackRate() || 1 : 1;
};

const getDuration = (): number => {
  return player.value ? player.value.duration() : 0;
};

// 弹幕相关方法
const sendDanmu = (danmu: DanmuMessage) => {
  if (player.value?.tcplayerBarrage) {
    // 按照官方示例的格式发送弹幕
    // 注意：send() 方法用于即时发送，不需要 stime
    const barrageData = {
      mode: 1, // 滚动模式
      text: danmu.text,
      size: danmu.fontSize || 25,
      color: danmu.color || "#FFFFFF",
    };

    try {
      player.value.tcplayerBarrage.send(barrageData);
      console.log("弹幕已发送:", barrageData);

      // 验证弹幕插件状态
      console.log("弹幕插件状态 - 已初始化:", !!player.value.tcplayerBarrage);
    } catch (error) {
      console.error("发送弹幕时出错:", error);
    }
  } else {
    console.error(
      "弹幕插件未初始化 - player.value:",
      !!player.value,
      "tcplayerBarrage:",
      !!player.value?.tcplayerBarrage,
    );
  }
};

const clearDanmu = () => {
  if (player.value?.tcplayerBarrage) {
    player.value.tcplayerBarrage.clear();
    console.log("弹幕已清空");
  } else {
    console.warn("弹幕插件未初始化");
  }
};

const enableDanmu = () => {
  if (player.value?.tcplayerBarrage) {
    player.value.tcplayerBarrage.start();
    console.log("弹幕已开启");
  } else {
    console.warn("弹幕插件未初始化");
  }
};

const disableDanmu = () => {
  if (player.value?.tcplayerBarrage) {
    player.value.tcplayerBarrage.stop();
    console.log("弹幕已关闭");
  } else {
    console.warn("弹幕插件未初始化");
  }
};

// 调试方法：检查弹幕插件状态
const checkBarrageStatus = () => {
  console.log("=== 弹幕插件状态检查 ===");
  console.log("播放器实例:", !!player.value);
  console.log("弹幕插件实例:", !!player.value?.tcplayerBarrage);
  console.log(
    "TcplayerBarragePlugin 全局可用:",
    typeof window.TcplayerBarragePlugin !== "undefined",
  );
  console.log("showDanmu prop:", props.showDanmu);

  if (player.value?.tcplayerBarrage) {
    console.log("✅ 弹幕插件已初始化，可以发送弹幕");
  } else {
    console.error("❌ 弹幕插件未初始化");
    if (!props.showDanmu) {
      console.log("提示: showDanmu 为 false");
    }
    if (typeof window.TcplayerBarragePlugin === "undefined") {
      console.log("提示: TcplayerBarragePlugin 未加载");
    }
  }
  console.log("========================");
};

// 暴露方法给父组件
defineExpose({
  player: player,
  play,
  pause,
  stop,
  setCurrentTime,
  getCurrentTime,
  setVolume,
  getVolume,
  setMuted,
  isMuted: getIsMuted,
  unmute, // 新增取消静音方法
  setPlaybackRate,
  getPlaybackRate,
  getDuration,
  sendDanmu,
  clearDanmu,
  enableDanmu,
  disableDanmu,
  checkBarrageStatus, // 添加调试方法
  isReady: () => isReady.value,
});

// 生命周期
onMounted(() => {
  nextTick(() => {
    initPlayer();
  });
  io = new IntersectionObserver(
    (entries: any) => {
      const e = entries[0];
      const visible = e.isIntersecting && e.intersectionRatio > 0;
      isInView.value = visible;
      isFloating.value = !visible;
      log("io", {
        visible,
        ratio: e.intersectionRatio,
        isFloating: isFloating.value,
      });
    },
    { threshold: [0, 0.1, 0.25] },
  );
  if (wrapperRef.value) io.observe(wrapperRef.value);
  keyHandler = (e: KeyboardEvent) => {
    if (e.key === "d") debugHudVisible.value = !debugHudVisible.value;
  };
  window.addEventListener("keydown", keyHandler);
});

onBeforeUnmount(() => {
  if (io && wrapperRef.value) {
    try {
      io.unobserve(wrapperRef.value);
    } catch {}
    io = null;
  }
  if (player.value) {
    try {
      // 检查 destroy 方法是否存在且是函数
      if (typeof player.value.destroy === "function") {
        player.value.destroy();
      } else {
        console.warn("Player destroy method is not available");
      }
    } catch (e) {
      console.warn("Error destroying player:", e);
    } finally {
      player.value = null;
    }
  }
  destroyMiniPlayer();
  if (keyHandler) window.removeEventListener("keydown", keyHandler);
});
</script>

<style scoped>
.mini-player :deep(.vjs-control-bar),
.mini-player :deep(.vjs-big-play-button),
.mini-player :deep(.vjs-progress-control),
.mini-player :deep(.vjs-volume-panel),
.mini-player :deep(.vjs-remaining-time),
.mini-player :deep(.vjs-current-time),
.mini-player :deep(.vjs-time-divider),
.mini-player :deep(.vjs-fullscreen-control) {
  display: none !important;
}
.tc-player-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.player-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
  z-index: 999;
  padding: 12px 20px;
  transition: all 0.2s ease-out;
  transform: translateY(-100%);
  opacity: 0;
}

.player-header.show {
  transform: translateY(0);
  opacity: 1;
  transition: all 0.3s ease-out;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 100%;
  transition: all 0.2s ease-out;
  transform: translateY(-10px);
  opacity: 0;
}

.player-header.show .header-content {
  transform: translateY(0);
  opacity: 1;
  transition: all 0.3s ease-out;
  transition-delay: 0.1s;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease-out;
  transform: translateX(-20px);
  opacity: 0;
}

.player-header.show .header-left {
  transform: translateX(0);
  opacity: 1;
  transition: all 0.3s ease-out;
  transition-delay: 0.2s;
}

.video-title {
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  transition: all 0.3s ease;
}

.live-indicator {
  color: #ff4444;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease-out;
  transform: translateX(20px);
  opacity: 0;
}

.player-header.show .header-right {
  transform: translateX(0);
  opacity: 1;
  transition: all 0.3s ease-out;
  transition-delay: 0.3s;
}

.viewer-count {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #ffffff;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.1);
  padding: 6px 12px;
  border-radius: 16px;
  transition: all 0.2s ease-out;
  transform: scale(0.8);
  opacity: 0;
}

.player-header.show .viewer-count {
  transform: scale(1);
  opacity: 1;
  transition: all 0.3s ease-out;
  transition-delay: 0.4s;
}

.viewer-count:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.viewer-icon {
  width: 16px;
  height: 16px;
  color: #ffffff;
  transition: all 0.3s ease;
}

.quality-selector {
  position: relative;
  transition: all 0.2s ease-out;
  transform: scale(0.8);
  opacity: 0;
}

.player-header.show .quality-selector {
  transform: scale(1);
  opacity: 1;
  transition: all 0.3s ease-out;
  transition-delay: 0.5s;
}

.quality-select {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #ffffff;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
}

.quality-select:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.quality-select option {
  background: #1a1a1a;
  color: #ffffff;
}

/* 加载动画样式 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
}

.loading-spinner {
  position: relative;
  width: 60px;
  height: 60px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid #ff4444;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  border-top-color: #ff6666;
  animation-delay: -0.4s;
}

.spinner-ring:nth-child(3) {
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  border-top-color: #ff8888;
  animation-delay: -0.8s;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.loading-progress {
  width: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff4444, #ff6666);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* 覆盖层容器 */
.overlay-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* background: rgba(0, 0, 0, 0.7); */
  /* z-index: 1000; */
  /* transition: all 0.3s ease; */
}

/* 进入直播间按钮容器（水平垂直居中） */
.enter-button-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, calc(-50% + 20px));
  opacity: 0;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  pointer-events: none;
}

.tc-player-wrapper:hover .enter-button-wrapper {
  opacity: 1;
  transform: translate(-50%, -50%);
  pointer-events: auto;
}

/* 进入直播间按钮 */
.enter-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background: rgba(220, 38, 38, 0.9);
  border: 2px solid #dc2626;
  border-radius: 12px;
  padding: 16px 48px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.4);
  gap: 12px;
  cursor: pointer;
}

.enter-button:hover {
  background: rgba(220, 38, 38, 1);
  border-color: #b91c1c;
  transform: scale(1.05) translateY(-2px);
  box-shadow: 0 12px 28px rgba(220, 38, 38, 0.5);
}

/* 取消静音按钮容器（底部居中） */
.unmute-button-wrapper {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
}

/* 取消静音按钮 */
.unmute-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background: rgba(220, 38, 38, 0.9);
  border: 2px solid #dc2626;
  border-radius: 12px;
  padding: 16px 48px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.4);
  gap: 12px;
  cursor: pointer;
}

.unmute-button:hover {
  background: rgba(220, 38, 38, 1);
  border-color: #b91c1c;
  transform: scale(1.05) translateY(-2px);
  box-shadow: 0 12px 28px rgba(220, 38, 38, 0.5);
}

/* 按钮通用样式 */
.button-icon {
  width: 28px;
  height: 28px;
  color: #ffffff;
}

.button-text {
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  user-select: none;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .enter-button,
  .unmute-button {
    padding: 12px 32px;
  }

  .button-text {
    font-size: 16px;
  }

  .button-icon {
    width: 24px;
    height: 24px;
  }

  .unmute-button-wrapper {
    bottom: 30px;
  }

  .player-header {
    padding: 10px 16px;
  }

  .video-title {
    font-size: 16px;
  }

  .live-indicator {
    font-size: 12px;
  }

  .viewer-count {
    font-size: 12px;
    padding: 4px 8px;
  }

  .viewer-icon {
    width: 14px;
    height: 14px;
  }

  .quality-select {
    font-size: 12px;
    padding: 4px 8px;
  }

  .unmute-overlay {
    padding: 15px;
  }

  .unmute-button {
    padding: 10px 16px;
    gap: 6px;
  }

  .unmute-icon {
    width: 20px;
    height: 20px;
  }

  .unmute-text {
    font-size: 12px;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
  }

  .loading-text {
    font-size: 16px;
  }

  .loading-progress {
    width: 160px;
  }

  .progress-text {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .player-header {
    padding: 8px 12px;
  }

  .header-content {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .header-right {
    align-self: flex-end;
    gap: 12px;
  }

  .video-title {
    font-size: 14px;
  }

  .live-indicator {
    font-size: 11px;
  }

  .viewer-count {
    font-size: 11px;
    padding: 3px 6px;
  }

  .viewer-icon {
    width: 12px;
    height: 12px;
  }

  .quality-select {
    font-size: 11px;
    padding: 3px 6px;
  }

  .unmute-overlay {
    padding: 12px;
  }

  .unmute-button {
    padding: 8px 12px;
    gap: 4px;
  }

  .unmute-icon {
    width: 18px;
    height: 18px;
  }

  .unmute-text {
    font-size: 11px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
  }

  .loading-text {
    font-size: 14px;
  }

  .loading-progress {
    width: 140px;
  }

  .progress-text {
    font-size: 11px;
  }
}

.tc-player-wrapper video {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain;
  display: block;
}

/* 确保TCPlayer容器正确填充 */
.tc-player-wrapper :deep(.tcplayer) {
  width: 100% !important;
  height: 100% !important;
  border-radius: 0 !important;
}

.tc-player-wrapper :deep(.tcplayer video) {
  width: 100% !important;
  height: 100% !important;
}

/* 隐藏播放器控制栏（当controls为false时） */
.tc-player-wrapper.hide-controls :deep(.vjs-control-bar) {
  display: none !important;
}

.tc-player-wrapper.hide-controls :deep(.vjs-big-play-button) {
  display: none !important;
}

.tc-player-wrapper.hide-controls :deep(.vjs-loading-spinner) {
  display: none !important;
}
</style>
